import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Download, FileBarChart, RefreshCw } from 'lucide-react'
import reportService, {
  AnonymizationDailyRow,
  CollectionTrendRow,
  RawCollectionReportSummary,
} from '../services/reportService'

// ─── 상수 ────────────────────────────────────────────────────────────────────

const OBJECT_LABELS: Record<string, string> = { face: '얼굴', plate: '번호판' }
const OBJECT_COLORS: Record<string, string> = { face: '#3b82f6', plate: '#f97316' }

const QUALITY_LABELS: Record<string, string> = {
  invalid_image: '이미지 파일 깨짐',
  image_decode_failed: '이미지 디코딩 실패',
  invalid_json: 'JSON 파일 깨짐',
  invalid_json_encoding: 'JSON 인코딩 오류',
  metadata_not_object: '메타데이터 형식 오류',
  ingest_error: '수집 처리 오류',
}

const today = () => new Date().toISOString().slice(0, 10)
const daysAgo = (n: number) => {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d.toISOString().slice(0, 10)
}
const fmt = (v: number) => v.toLocaleString()

// Y축 눈금 계산 (4단계)
function calcTicks(maxVal: number, unit: number = 0): [number, number[]] {
  const base = unit > 0 ? unit : Math.max(1, Math.pow(10, Math.floor(Math.log10(maxVal / 4))))
  const step = Math.max(base, Math.ceil(maxVal / 4 / base) * base)
  const max = step * 4
  return [max, [0, step, step * 2, step * 3, max]]
}

// ─── 차트 컴포넌트 ────────────────────────────────────────────────────────────

/** 일별 수직 막대 차트 */
const DailyBarChart: React.FC<{ data: CollectionTrendRow[] }> = ({ data }) => {
  const [hovered, setHovered] = useState<CollectionTrendRow | null>(null)
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null)
  const plotRef = useRef<HTMLDivElement | null>(null)

  if (data.length === 0) {
    return <div className="flex h-52 items-center justify-center text-sm text-slate-400">데이터 없음</div>
  }

  const maxCount = Math.max(...data.map((d) => d.count), 1)
  const [maxTick, ticks] = calcTicks(maxCount)

  const BAR_AREA_H = 152  // 막대 영역 고정 픽셀 높이
  const X_LABEL_H = 20    // X축 레이블 행 높이

  const labelStep = data.length <= 14 ? 2 : data.length <= 31 ? 7 : 14

  return (
    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3">
      <div className="flex items-start">
        {/* Y축: 막대 영역 높이에만 맞춤 */}
        <div
          className="flex shrink-0 flex-col justify-between pr-2 text-[10px] text-slate-400"
          style={{ width: 44, height: BAR_AREA_H }}
        >
          {[...ticks].reverse().map((t) => (
            <span key={t} className="text-right leading-none">{t.toLocaleString()}</span>
          ))}
        </div>

        {/* 플롯 영역 */}
        <div
          ref={plotRef}
          className="relative flex-1"
          style={{ height: BAR_AREA_H + X_LABEL_H }}
        >
          {/* 그리드 라인: 막대 영역에만 */}
          <div
            className="pointer-events-none absolute inset-x-0 top-0 flex flex-col justify-between"
            style={{ height: BAR_AREA_H }}
          >
            {[...ticks].reverse().map((t) => (
              <div
                key={t}
                className={t === 0 ? 'border-t border-slate-300' : 'border-t border-dashed border-slate-200'}
              />
            ))}
          </div>

          {/* 막대 행: 막대 영역 고정 높이 */}
          <div
            className="absolute inset-x-0 top-0 flex items-end gap-px"
            style={{ height: BAR_AREA_H }}
          >
            {data.map((d) => {
              const barH = Math.max(d.count > 0 ? 3 : 1, Math.round((d.count / maxTick) * (BAR_AREA_H - 2)))
              return (
                <div
                  key={d.date}
                  className="flex flex-1 cursor-default items-end"
                  style={{ height: BAR_AREA_H }}
                  onMouseEnter={() => setHovered(d)}
                  onMouseMove={(e) => {
                    const rect = plotRef.current?.getBoundingClientRect()
                    if (rect) setTooltipPos({ x: e.clientX - rect.left + 12, y: e.clientY - rect.top + 12 })
                  }}
                  onMouseLeave={() => { setHovered(null); setTooltipPos(null) }}
                >
                  <div
                    className={`w-full rounded-t-[2px] ${d.count > 0 ? 'bg-blue-500 hover:bg-blue-400' : 'bg-blue-100'}`}
                    style={{ height: barH }}
                  />
                </div>
              )
            })}
          </div>

          {/* X축 레이블 행: 막대 행 아래에 완전히 분리 */}
          <div
            className="absolute inset-x-0 flex gap-px"
            style={{ top: BAR_AREA_H, height: X_LABEL_H }}
          >
            {data.map((d, i) => {
              const show = i === 0 || i === data.length - 1 || i % labelStep === 0
              return (
                <div
                  key={d.date}
                  className="relative flex flex-1 justify-center"
                >
                  {show && (
                    <span className="absolute top-1 whitespace-nowrap text-[9px] text-slate-400">
                      {d.date.slice(5)}
                    </span>
                  )}
                </div>
              )
            })}
          </div>

          {/* 툴팁 */}
          {hovered && tooltipPos && (() => {
            const cw = plotRef.current?.offsetWidth ?? 9999
            const flipX = tooltipPos.x + 140 > cw
            return (
              <div
                className="pointer-events-none absolute z-10 whitespace-nowrap rounded bg-slate-800 px-2 py-1.5 text-xs text-white shadow-lg"
                style={flipX
                  ? { right: cw - tooltipPos.x + 12, top: tooltipPos.y }
                  : { left: tooltipPos.x, top: tooltipPos.y }}
              >
                <div className="font-semibold">{hovered.date}</div>
                <div className="text-slate-300">수집 {fmt(hovered.count)}건</div>
                <div className="text-slate-400">누적 {fmt(hovered.cumulative)}건</div>
              </div>
            )
          })()}
        </div>
      </div>
    </div>
  )
}

/** SVG 단일 라인 차트 (누적 추이) */
const CumulativeLineChart: React.FC<{ data: CollectionTrendRow[] }> = ({ data }) => {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null)
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const W = 520
  const H = 180
  const PAD = { top: 16, right: 16, bottom: 30, left: 54 }
  const pw = W - PAD.left - PAD.right
  const ph = H - PAD.top - PAD.bottom

  if (data.length === 0) {
    return <div className="flex h-48 items-center justify-center text-sm text-slate-400">데이터 없음</div>
  }

  const maxVal = Math.max(...data.map((d) => d.cumulative), 1)
  const [yMax, yTicks] = calcTicks(maxVal)

  const toX = (i: number) => PAD.left + (i / Math.max(data.length - 1, 1)) * pw
  const toY = (v: number) => PAD.top + ph - (v / yMax) * ph

  const linePts = data.map((d, i) => `${toX(i)},${toY(d.cumulative)}`).join(' ')
  const areaPts = `${PAD.left},${PAD.top + ph} ${linePts} ${toX(data.length - 1)},${PAD.top + ph}`

  const labelStep = Math.max(1, Math.ceil(data.length / 6))
  const xLabels = data
    .map((d, i) => ({ d, i }))
    .filter(({ i }) => i === 0 || i === data.length - 1 || i % labelStep === 0)

  const handleMouseMove = (e: React.MouseEvent<SVGRectElement>) => {
    const svg = svgRef.current
    const container = containerRef.current
    if (!svg || !container) return
    // getScreenCTM로 SVG 내부 좌표로 정확히 변환 (preserveAspectRatio 보정 포함)
    const pt = svg.createSVGPoint()
    pt.x = e.clientX
    pt.y = e.clientY
    const svgPt = pt.matrixTransform(svg.getScreenCTM()!.inverse())
    const idx = Math.round(((svgPt.x - PAD.left) / pw) * (data.length - 1))
    setHoveredIdx(Math.max(0, Math.min(data.length - 1, idx)))
    const containerRect = container.getBoundingClientRect()
    setTooltipPos({ x: e.clientX - containerRect.left, y: e.clientY - containerRect.top })
  }

  const hovered = hoveredIdx !== null ? data[hoveredIdx] : null

  return (
    <div ref={containerRef} className="relative">
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 192 }}>
        {/* 그리드 + Y축 */}
        {yTicks.map((v) => {
          const y = toY(v)
          return (
            <g key={v}>
              <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y}
                stroke={v === 0 ? '#94a3b8' : '#e2e8f0'} strokeWidth={1}
                strokeDasharray={v === 0 ? undefined : '4 3'} />
              <text x={PAD.left - 4} y={y + 4} textAnchor="end" fontSize={10} fill="#94a3b8">
                {v.toLocaleString()}
              </text>
            </g>
          )
        })}
        {/* 영역 + 라인 */}
        <polygon points={areaPts} fill="#3b82f6" fillOpacity={0.08} />
        <polyline points={linePts} fill="none" stroke="#3b82f6" strokeWidth={2} strokeLinejoin="round" />
        {/* X축 레이블 */}
        {xLabels.map(({ d, i }) => (
          <text key={d.date} x={toX(i)} y={H - 4} textAnchor="middle" fontSize={10} fill="#94a3b8">
            {d.date.slice(5)}
          </text>
        ))}
        {/* 호버: 수직선 + 점 */}
        {hovered !== null && hoveredIdx !== null && (
          <g>
            <line
              x1={toX(hoveredIdx)} y1={PAD.top}
              x2={toX(hoveredIdx)} y2={PAD.top + ph}
              stroke="#94a3b8" strokeWidth={1} strokeDasharray="4 3"
            />
            <circle
              cx={toX(hoveredIdx)} cy={toY(hovered.cumulative)}
              r={4} fill="#3b82f6" stroke="#fff" strokeWidth={2}
            />
          </g>
        )}
        {/* 마우스 이벤트 캡처용 투명 rect */}
        <rect
          x={PAD.left} y={PAD.top} width={pw} height={ph}
          fill="transparent" style={{ cursor: 'crosshair' }}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => { setHoveredIdx(null); setTooltipPos(null) }}
        />
      </svg>
      {/* div 툴팁 */}
      {hovered && tooltipPos && (
        <div
          className="pointer-events-none absolute z-10 rounded bg-slate-800 px-2 py-1.5 text-xs text-white shadow-lg"
          style={{ left: tooltipPos.x + 12, top: tooltipPos.y + 12, maxWidth: 150 }}
        >
          <div className="font-semibold">{hovered.date}</div>
          <div className="text-slate-300">일 수집 {fmt(hovered.count)}건</div>
          <div className="text-blue-300">누적 {fmt(hovered.cumulative)}건</div>
        </div>
      )}
    </div>
  )
}

/** SVG 2-시리즈 라인 차트 (얼굴/번호판) */
const TwoSeriesLineChart: React.FC<{
  data: AnonymizationDailyRow[]
  seriesKeys: string[]
}> = ({ data, seriesKeys }) => {
  const W = 520
  const H = 180
  const PAD = { top: 28, right: 16, bottom: 30, left: 54 }
  const pw = W - PAD.left - PAD.right
  const ph = H - PAD.top - PAD.bottom

  if (data.length === 0 || seriesKeys.length === 0) {
    return <div className="flex h-48 items-center justify-center text-sm text-slate-400">데이터 없음</div>
  }

  const maxVal = Math.max(...data.flatMap((d) => seriesKeys.map((k) => d.counts[k] || 0)), 1)
  const [yMax, yTicks] = calcTicks(maxVal)

  const toX = (i: number) => PAD.left + (i / Math.max(data.length - 1, 1)) * pw
  const toY = (v: number) => PAD.top + ph - (v / yMax) * ph

  const labelStep = Math.max(1, Math.ceil(data.length / 6))
  const xLabels = data
    .map((d, i) => ({ d, i }))
    .filter(({ i }) => i === 0 || i === data.length - 1 || i % labelStep === 0)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 192 }}>
      {/* 범례 */}
      {seriesKeys.map((k, si) => (
        <g key={k}>
          <rect x={PAD.left + si * 72} y={6} width={12} height={8} rx={2}
            fill={OBJECT_COLORS[k] || '#94a3b8'} />
          <text x={PAD.left + si * 72 + 16} y={14} fontSize={10} fill="#64748b">
            {OBJECT_LABELS[k] || k}
          </text>
        </g>
      ))}
      {/* 그리드 */}
      {yTicks.map((v) => {
        const y = toY(v)
        return (
          <g key={v}>
            <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y}
              stroke={v === 0 ? '#94a3b8' : '#e2e8f0'} strokeWidth={1}
              strokeDasharray={v === 0 ? undefined : '4 3'} />
            <text x={PAD.left - 4} y={y + 4} textAnchor="end" fontSize={10} fill="#94a3b8">
              {v.toLocaleString()}
            </text>
          </g>
        )
      })}
      {/* 시리즈 */}
      {seriesKeys.map((k) => {
        const pts = data.map((d, i) => `${toX(i)},${toY(d.counts[k] || 0)}`).join(' ')
        return (
          <polyline key={k} points={pts} fill="none"
            stroke={OBJECT_COLORS[k] || '#94a3b8'} strokeWidth={2} strokeLinejoin="round" />
        )
      })}
      {/* X축 레이블 */}
      {xLabels.map(({ d, i }) => (
        <text key={d.date} x={toX(i)} y={H - 4} textAnchor="middle" fontSize={10} fill="#94a3b8">
          {d.date.slice(5)}
        </text>
      ))}
    </svg>
  )
}

/** 수평 막대 차트 */
const HorizontalBarChart: React.FC<{
  data: { label: string; count: number; sub?: string }[]
  color?: 'blue' | 'red' | 'green' | 'orange'
  maxRows?: number
}> = ({ data, color = 'blue', maxRows = 12 }) => {
  const rows = data.slice(0, maxRows)
  const maxCount = Math.max(...rows.map((r) => r.count), 1)

  const barColor = {
    blue: 'bg-blue-500',
    red: 'bg-red-400',
    green: 'bg-emerald-500',
    orange: 'bg-orange-400',
  }[color]

  if (rows.length === 0) {
    return <div className="flex min-h-[192px] items-center justify-center text-sm text-slate-400">데이터 없음</div>
  }

  return (
    <div className="space-y-1.5">
      {rows.map((row, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-28 shrink-0 truncate text-right text-xs text-slate-600" title={row.label}>
            {row.label}
          </div>
          <div className="relative h-5 flex-1 overflow-hidden rounded-sm bg-slate-100">
            <div
              className={`h-full rounded-sm ${barColor} transition-all`}
              style={{ width: `${(row.count / maxCount) * 100}%` }}
            />
          </div>
          <div className="w-14 shrink-0 text-right text-xs font-semibold text-slate-700">
            {fmt(row.count)}
          </div>
          {row.sub !== undefined && (
            <div className="w-12 shrink-0 text-right text-[10px] text-slate-400">{row.sub}</div>
          )}
        </div>
      ))}
    </div>
  )
}

// ─── 레이아웃 컴포넌트 ────────────────────────────────────────────────────────

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-6">
    <div className="mb-3 border-l-4 border-blue-500 pl-3 text-sm font-bold text-slate-800">{title}</div>
    {children}
  </div>
)

const Card: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({
  title, children, className = '',
}) => (
  <div className={`rounded-lg border border-slate-200 bg-white p-4 ${className}`}>
    <div className="mb-3 text-sm font-semibold text-slate-600">{title}</div>
    {children}
  </div>
)


const LoadingBlock: React.FC<{ height?: string }> = ({ height = 'h-48' }) => (
  <div className={`flex ${height} items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50`}>
    <div className="flex items-center gap-2 text-sm text-slate-400">
      <RefreshCw size={15} className="animate-spin" />
      조회 중
    </div>
  </div>
)

const KpiCard: React.FC<{
  title: string
  value: number | string
  unit?: string
  accent?: 'default' | 'green' | 'red' | 'amber'
  sub?: string
}> = ({ title, value, unit, accent = 'default', sub }) => {
  const valueColor = {
    default: 'text-slate-900',
    green: 'text-emerald-600',
    red: 'text-red-500',
    amber: 'text-amber-500',
  }[accent]
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-xs font-semibold text-slate-500">{title}</div>
      <div className={`mt-2 text-2xl font-bold ${valueColor}`}>
        {typeof value === 'number' ? fmt(value) : value}
        {unit && <span className="ml-1 text-sm font-normal text-slate-400">{unit}</span>}
      </div>
      {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
    </div>
  )
}

const QualityGauge: React.FC<{
  passRate: number
  passCount: number
  failCount: number
}> = ({ passRate, passCount, failCount }) => {
  const accent = passRate >= 99 ? 'text-emerald-600' : passRate >= 95 ? 'text-amber-500' : 'text-red-500'
  const bar = passRate >= 99 ? 'bg-emerald-500' : passRate >= 95 ? 'bg-amber-400' : 'bg-red-400'
  return (
    <div className="mb-4">
      <span className={`text-3xl font-bold ${accent}`}>{passRate.toFixed(1)}</span>
      <span className={`ml-1 text-base font-semibold ${accent}`}>%</span>
      <div className="mt-1 text-xs text-slate-500">
        통과 {fmt(passCount)}건 / 실패 {fmt(failCount)}건
      </div>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={`h-2 rounded-full ${bar}`} style={{ width: `${Math.min(passRate, 100)}%` }} />
      </div>
    </div>
  )
}

// ─── 메인 페이지 ──────────────────────────────────────────────────────────────

interface Props {
  userInfo: {
    email: string
    fullName: string
    isAdmin: boolean
    permissions: string[]
  }
}

const MAX_DAYS = 365

const RawCollectionReport: React.FC<Props> = () => {
  const [startDate, setStartDate] = useState(daysAgo(30))
  const [endDate, setEndDate] = useState(today())
  const [summary, setSummary] = useState<RawCollectionReportSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fetchSeq = useRef(0)
  const isFirstFetch = useRef(true)

  const hasDates = Boolean(startDate && endDate)
  const startTime = new Date(startDate).getTime()
  const endTime = new Date(endDate).getTime()
  const daysDiff = Math.round((endTime - startTime) / 86400000)
  const rangeError = !hasDates
    ? null
    : startTime > endTime
      ? '시작일은 종료일보다 늦을 수 없습니다.'
      : daysDiff > MAX_DAYS
        ? `최대 ${MAX_DAYS}일까지 조회할 수 있습니다. (현재 ${daysDiff}일)`
        : null
  const canQuery = hasDates && !rangeError

  const fetchSummary = async () => {
    if (!canQuery) return
    // 응답 역전 방지: 최신 요청의 응답만 반영
    const seq = ++fetchSeq.current
    setLoading(true)
    setError(null)
    try {
      const data = await reportService.getRawCollectionSummary(startDate, endDate)
      if (seq === fetchSeq.current) setSummary(data)
    } catch (e: any) {
      if (seq === fetchSeq.current) setError(e?.message || '리포트를 불러오지 못했습니다.')
    } finally {
      if (seq === fetchSeq.current) setLoading(false)
    }
  }

  // 날짜 변경 시 자동 조회 (최초 진입은 즉시, 이후 변경은 400ms 디바운스)
  useEffect(() => {
    if (!canQuery) return
    if (isFirstFetch.current) {
      isFirstFetch.current = false
      fetchSummary()
      return
    }
    const timer = setTimeout(fetchSummary, 400)
    return () => clearTimeout(timer)
  }, [startDate, endDate]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = async () => {
    if (!canQuery) return
    setDownloading(true)
    setError(null)
    try {
      await reportService.downloadRawCollectionReport(startDate, endDate)
    } catch (e: any) {
      setError(e?.message || '다운로드에 실패했습니다.')
    } finally {
      setDownloading(false)
    }
  }

  const isInitialLoading = loading && !summary
  const isRefetching = loading && !!summary
  const kpi = summary?.kpi
  const trend = summary?.collection_trend || []

  // 마지막 성공 조회의 기간 (화면·다운로드가 같은 기간임을 명시)
  const appliedPeriod = summary?.period
  const appliedDays = appliedPeriod
    ? Math.round(
        (new Date(appliedPeriod.end_date).getTime() - new Date(appliedPeriod.start_date).getTime()) / 86400000,
      ) + 1
    : 0

  // 어디서 모였나
  const vehicleBarData = useMemo(() =>
    (summary?.vehicle_collection || []).slice(0, 15).map((r) => ({
      label: r.vehicle_no,
      count: r.count,
    })), [summary])

  const triggerBarData = useMemo(() =>
    (summary?.trigger_collection || []).map((r) => ({
      label: r.trigger_label !== '미상' ? r.trigger_label : r.trigger_id,
      count: r.count,
    })), [summary])

  // 건강한가 — 실패 사유
  const qualityBarData = useMemo(() =>
    (summary?.quality || []).map((r) => ({
      label: QUALITY_LABELS[r.reason] || r.reason,
      count: r.count,
    })), [summary])

  // 건강한가 — 차량별 정합성
  const vehicleFailData = useMemo(() =>
    (summary?.vehicle_collection || [])
      .filter((r) => r.quality_failed > 0)
      .sort((a, b) => b.quality_failed - a.quality_failed)
      .slice(0, 10)
      .map((r) => ({
        label: r.vehicle_no,
        count: r.quality_failed,
        sub: r.count > 0 ? `${((r.quality_failed / r.count) * 100).toFixed(1)}%` : '-',
      })), [summary])

  // 무엇이 처리됐나
  const objectKeys = useMemo(() => {
    const keys = new Set<string>()
    summary?.anonymization_daily?.forEach((row) => Object.keys(row.counts).forEach((k) => keys.add(k)))
    return Array.from(keys).sort()
  }, [summary])

  const anonymObjectData = useMemo(() =>
    (summary?.anonymization_objects || []).map((r) => ({
      label: OBJECT_LABELS[r.object_type] || r.object_type,
      count: r.count,
      sub: (() => {
        const total = (summary?.anonymization_objects || []).reduce((s, x) => s + x.count, 0)
        return total > 0 ? `${((r.count / total) * 100).toFixed(1)}%` : '-'
      })(),
    })), [summary])

  const passRate = kpi?.quality_pass_rate ?? 100
  const passAccent: 'green' | 'amber' | 'red' =
    passRate >= 99 ? 'green' : passRate >= 95 ? 'amber' : 'red'

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      {/* 헤더 */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xl font-bold text-slate-900">
            <FileBarChart size={22} /> 기간별 자동수집 리포트
          </div>
          <div className="mt-1 text-sm text-slate-500">수집일 기준 자동수집 데이터 집계</div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs font-semibold text-slate-500">
            시작일
            <input
              className="mt-1 block rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
              type="date" value={startDate} max={endDate || today()}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>
          <label className="text-xs font-semibold text-slate-500">
            종료일
            <input
              className="mt-1 block rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
              type="date" value={endDate} min={startDate || undefined} max={today()}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
          <button
            className="inline-flex items-center gap-2 rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            onClick={fetchSummary} disabled={loading || !canQuery}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            {loading ? '조회 중' : '새로고침'}
          </button>
          <button
            className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 disabled:opacity-50"
            onClick={handleDownload} disabled={downloading || loading || !summary || !canQuery}
          >
            <Download size={16} /> {downloading ? '다운로드 중' : 'XLSX'}
          </button>
        </div>
      </div>

      {rangeError && (
        <div className="mb-4 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          {rangeError}
        </div>
      )}

      {error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* 표시 중인 기간 */}
      {appliedPeriod && (
        <div className="mb-3 flex items-center gap-2 text-xs text-slate-500">
          <span className="font-semibold">표시 중인 기간</span>
          <span>
            {appliedPeriod.start_date} ~ {appliedPeriod.end_date} ({appliedDays}일)
          </span>
          {isRefetching && <RefreshCw size={12} className="animate-spin text-blue-500" />}
        </div>
      )}

      {/* 재조회 중에는 이전 결과를 유지한 채 dim 처리 */}
      <div className={isRefetching ? 'pointer-events-none opacity-60 transition-opacity' : 'transition-opacity'}>

      {/* KPI */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard title="수집 이미지" value={isInitialLoading ? '-' : (kpi?.collected_images || 0)} unit={isInitialLoading ? undefined : '건'} />
        <KpiCard
          title="정합성 통과율"
          value={isInitialLoading ? '-' : `${(kpi?.quality_pass_rate ?? 100).toFixed(1)}%`}
          accent={passAccent}
          sub={isInitialLoading ? '조회 중' : `실패 ${fmt(kpi?.quality_failed || 0)}건`}
        />
        <KpiCard title="수집 차량" value={isInitialLoading ? '-' : (kpi?.vehicles || 0)} unit={isInitialLoading ? undefined : '대'} />
        <KpiCard title="비식별화 객체" value={isInitialLoading ? '-' : (kpi?.anonymized_objects || 0)} unit={isInitialLoading ? undefined : '건'} />
      </div>

      {/* 수집 현황 */}
      <Section title="수집 현황">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card title="일별 수집건수">
            {isInitialLoading ? <LoadingBlock height="h-52" /> : <DailyBarChart data={trend} />}
          </Card>
          <Card title="누적 수집 추이">
            {isInitialLoading ? <LoadingBlock /> : <CumulativeLineChart data={trend} />}
          </Card>
        </div>
      </Section>

      {/* 수집 분포 */}
      <Section title="수집 분포">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card title={`차량별 수집 (Top ${Math.min(vehicleBarData.length, 15)})`}>
            {isInitialLoading ? <LoadingBlock /> : <HorizontalBarChart data={vehicleBarData} color="blue" />}
          </Card>
          <Card title="트리거별 수집">
            {isInitialLoading ? <LoadingBlock /> : <HorizontalBarChart data={triggerBarData} color="blue" />}
          </Card>
        </div>
      </Section>

      {/* 데이터 정합성 */}
      <Section title="데이터 정합성">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card title="정합성 현황">
            {isInitialLoading ? <LoadingBlock /> : <>
              <QualityGauge
                passRate={kpi?.quality_pass_rate ?? 100}
                passCount={summary?.quality_pass_count ?? kpi?.collected_images ?? 0}
                failCount={kpi?.quality_failed ?? 0}
              />
              {qualityBarData.length > 0 ? (
              <>
                <div className="mb-2 text-xs font-semibold text-slate-500">실패 사유</div>
                <HorizontalBarChart data={qualityBarData} color="red" />
              </>
              ) : (
                <div className="text-sm text-emerald-600">정합성 문제가 없습니다.</div>
              )}
            </>}
          </Card>
          <Card title="차량별 정합성 실패">
            {isInitialLoading ? <LoadingBlock /> : vehicleFailData.length > 0 ? (
              <HorizontalBarChart data={vehicleFailData} color="red" />
            ) : (
              <div className="flex min-h-[192px] items-center justify-center text-sm text-emerald-600">
                정합성 실패 차량이 없습니다.
              </div>
            )}
          </Card>
        </div>
      </Section>

      {/* 비식별화 현황 */}
      <Section title="비식별화 현황">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card title="일별 비식별화 추이">
            {isInitialLoading ? <LoadingBlock /> : <TwoSeriesLineChart data={summary?.anonymization_daily || []} seriesKeys={objectKeys} />}
          </Card>
          <Card title="객체분류별 합계">
            {isInitialLoading ? <LoadingBlock /> : <HorizontalBarChart data={anonymObjectData} color="orange" />}
          </Card>
        </div>
      </Section>

      </div>
    </div>
  )
}

export default RawCollectionReport
